// Rules for the 20-minute retail bill edit window.
// Mirrors ItemService.updateFreshBillWithinWindow on the backend, which has the
// final say — these helpers only decide what the UI offers.

export const BILL_EDIT_WINDOW_MINUTES = 20;
const WINDOW_MS = BILL_EDIT_WINDOW_MINUTES * 60 * 1000;

const pad = (value) => String(value).padStart(2, '0');

// yyyy-MM-dd for either an ISO/`yyyy-MM-dd` string or epoch millis
const toDatePart = (value) => {
  if (typeof value === 'number') {
    const d = new Date(value);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  return String(value).slice(0, 10);
};

// HH:mm:ss for either an `HH:mm:ss` string or epoch millis
const toTimePart = (value) => {
  if (typeof value === 'number') {
    const d = new Date(value);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  return String(value).slice(0, 8);
};

/**
 * When the bill was created. Uses the backend `createdAt` timestamp; bills saved
 * before that column existed fall back to bill_date + time (bill_date alone is
 * a DATE column and can't measure minutes).
 */
export const getBillCreatedAt = (bill) => {
  if (!bill) return null;

  if (bill.createdAt) {
    const created = new Date(bill.createdAt);
    if (!Number.isNaN(created.getTime())) return created;
  }

  if (bill.bill_date && bill.time) {
    const created = new Date(`${toDatePart(bill.bill_date)}T${toTimePart(bill.time)}`);
    if (!Number.isNaN(created.getTime())) return created;
  }

  return null;
};

/** Milliseconds left in the edit window (0 once it has ended or is unknown). */
export const getEditMsLeft = (bill, now = Date.now()) => {
  const created = getBillCreatedAt(bill);
  if (!created) return 0;
  return Math.max(0, created.getTime() + WINDOW_MS - now);
};

/** Only NEW purchase lines can be re-saved — return/exchange lines would be lost. */
export const hasOnlyNewItems = (bill) =>
  (bill?.bill || []).every((line) => !line.itemStatus || line.itemStatus === 'NEW');

/** Whether the Update action should be offered for this bill right now. */
export const canUpdateBill = (bill, now = Date.now()) =>
  !!bill &&
  bill.status === 'FRESH' &&
  (!bill.billType || bill.billType === 'RETAIL') &&
  hasOnlyNewItems(bill) &&
  getEditMsLeft(bill, now) > 0;

/** m:ss for a countdown */
export const formatTimeLeft = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${pad(seconds)}`;
};
